import * as STD from '@emersya/mpa-std';
import * as CM from '@emersya/mpa-components-manager';
import {Block, InputButton, InputSelect, Text} from '@emersya/mpa-components';
import {
	NS as PlannerFloorEditorNS
} from '@emersya/mpa-engine/src/app/client/components/viewer/planner/floor/Editor/PlannerFloorEditor';
import {NS as Application} from '../Application/Application';

class TotalExpoPlannerFloorEditorComponent extends PlannerFloorEditorNS.Component {

	isRaised: boolean = false;

	override async createDOM() {
		await this.createTypeSection();
		await this.createInputSelectSection();
	};

	override getInputSelect() {
		return <InputSelect.Component>this.component_find('@:content.materials.content.options');
	};

	private async createTypeSection() {
		const block = await this.component_find('@:content').component_instantiateOuterChild({
			type: 'block',
			isSlot: false,
			id: 'type',
			slot: null,
			tree: this.component_tree,
			parentNode: null,
			configuration: {}
		});
		await block.component_instantiateOuterChild({
			type: 'text',
			isSlot: false,
			id: 'title',
			slot: null,
			tree: this.component_tree,
			parentNode: null,
			configuration: <Block.Configuration>{
				'type': 'title-2'
			}
		});
		const content = await block.component_instantiateOuterChild({
			type: 'block',
			isSlot: false,
			id: 'content',
			slot: null,
			tree: this.component_tree,
			parentNode: null,
			configuration: {}
		});
		for (const type of ['raised', 'carpet', 'none']) {
			await content.component_instantiateOuterChild({
				type: 'input-button',
				isSlot: false,
				id: type,
				slot: null,
				tree: this.component_tree,
				parentNode: null,
				configuration: <InputButton.Configuration>{
					'@classList': type === 'none' ? ['selected'] : []
				}
			});
		}
	};

	private async createInputSelectSection() {
		const block = <Block.Component>await this.component_find('@:content').component_instantiateOuterChild({
			type: 'block',
			isSlot: false,
			id: 'materials',
			slot: null,
			tree: this.component_tree,
			parentNode: null,
			configuration: <Block.Configuration>{
				'@classList': ['hidden']
			}
		});
		await block.component_instantiateOuterChild({
			type: 'text',
			isSlot: false,
			id: 'title',
			slot: null,
			tree: this.component_tree,
			parentNode: null,
			configuration: <Text.Configuration>{
				'type': 'title-2'
			}
		});
		const content = await block.component_instantiateOuterChild({
			type: 'block',
			isSlot: false,
			id: 'content',
			slot: null,
			tree: this.component_tree,
			parentNode: null,
			configuration: {}
		});
		await content.component_instantiateOuterChild({
			type: 'text',
			isSlot: false,
			id: 'selected',
			slot: null,
			tree: this.component_tree,
			parentNode: null,
			configuration: {}
		});
		await content.component_instantiateOuterChild({
			type: 'input-select',
			isSlot: false,
			id: 'options',
			slot: null,
			tree: this.component_tree,
			parentNode: null,
			configuration: <InputSelect.Configuration>{
				isEditable: true,
				renderingMode: 'radio'
			}
		});
	};

	private async onTypeButton(event: STD.events.Event<any, CM.Component<any, any>>) {
		const raised = <InputButton.Component>this.component_find('@:content.type.content.raised');
		const carpet = <InputButton.Component>this.component_find('@:content.type.content.carpet');
		const none = <InputButton.Component>this.component_find('@:content.type.content.none');
		const wasRaised = raised.component_DOM.classList.contains('selected');
		if (event.target.id === 'raised') {
			raised.component_DOM.classList.toggle('selected');
		} else if (event.target.id === 'carpet') {
			carpet.component_DOM.classList.toggle('selected');
		} else {
			raised.component_DOM.classList.remove('selected');
			carpet.component_DOM.classList.remove('selected');
		}
		const isRaised = raised.component_DOM.classList.contains('selected');
		const materialsSection = <Block.Component>this.component_find('@:content.materials');
		const hasChanged = isRaised !== wasRaised;
		if (hasChanged) {
			this.isRaised = isRaised;
		}
		const currentType =
			(carpet.component_DOM.classList.contains('selected') && 'carpet') ||
			(isRaised && 'raised') ||
			'none';
		if (currentType !== 'none') {
			none.component_DOM.classList.remove('selected');
			materialsSection.component_DOM.classList.remove('hidden');
		} else {
			none.component_DOM.classList.add('selected');
			materialsSection.component_DOM.classList.add('hidden');
		}
		await this.filterMaterials(currentType);
		await (<Application.Component>app).setRaisedFloorVisibility(isRaised, hasChanged);
		if (hasChanged && !materialsSection.component_DOM.classList.contains('hidden')) {
			await app.basket?.dispatchEvent('basket-update', {
				subType: 'partPriceUpdated',
				details: {}
			});
		}
	};

	private async filterMaterials(type: 'raised' | 'carpet' | 'none') {
		const options = this.getInputSelect();
		let selectedButton: InputButton.Component | null = null;
		for (const button of <InputButton.Component[]>options.component_outerChilds) {
			const currentProduct = app.basket?.basket.currentProduct;
			if (!app.basket || !currentProduct) {
				this.component_throwLibraryError('', {});
			}
			const updatedProductSKU = app.currentProduct?.sku?.value;
			if (!updatedProductSKU) {
				this.component_throwUserError('Error while loading new sku product from basket', {});
			}
			const PMScurrentProduct = app.component_configuration.PMS.products.find(x => x.SKU === currentProduct.id);
			if (!PMScurrentProduct) {
				this.component_throwUserError('Missing PMS product for current product', {});
			}
			const floorConfiguration = PMScurrentProduct.configurations.find(x => (
				((x.context.forProducts.length === 1 && x.context.forProducts[0] === '*') || (x.context.forProducts.includes(updatedProductSKU))) &&
				x.SKU === '@floor'
			));
			if (!floorConfiguration) {
				this.component_throwUserError('No configuration with @floor SKU!', {});
			}
			const currentOption = floorConfiguration.context.options.find(x => x.SKU === button.id);
			if (!currentOption) {
				this.component_throwUserError('Cannot find options for this button', {id: button.id});
			}
			if ((<any>currentOption.metadata).floorType === type) {
				await button.component_enable();
				if (!selectedButton || button.component_DOM.classList.contains('selected')) {
					selectedButton = button;
				}
			} else {
				await button.component_disable();
			}
		}
		if (!app.isReloading && options.value !== selectedButton) {
			await options.updateValueFromId(selectedButton!.id);
		}
	};

	override async onValueUpdated(event: STD.events.Event<any, CM.Component<any, any>>) {
		if (event.subType === 'input-select') {
			await super.onValueUpdated(event);
			const selected = <Text.Component>this.component_find('@:content.materials.content.selected');
			const text = (<InputButton.Component>event.target.value).component_configuration['@translations']!.text![STD.translationsManager.language]!;
			selected.update(text);
			if (event.details.newValue) {
				if (!event.details.newValue.option.metaData) {
					event.details.newValue.option.metaData = {};
				}
				event.details.newValue.option.metaData.isRaised = this.isRaised;
			}
		} else {
			await this.onTypeButton(event);
		}
	};

	constructor(
		descriptor: ConstructorParameters<typeof CM.Component>[0],
		configuration: PlannerFloorEditorNS.Configuration
	) {
		super(descriptor, configuration);
		void this.onTypeButton;
	}
};

export namespace NS {
	export const Component = TotalExpoPlannerFloorEditorComponent;
	export type Component = TotalExpoPlannerFloorEditorComponent;
	export type Configuration = PlannerFloorEditorNS.Configuration;
}
import manifestXMLSource from './PlannerFloorEditor.xml';
import './PlannerFloorEditor.scss';

CM.Manager.declare({
	id: 'totalExpo-viewer-planner-floor-editor',
	type: TotalExpoPlannerFloorEditorComponent,
	manifest: manifestXMLSource
});
